import * as React from 'react'
import { useCallback, useEffect, useMemo } from 'react'
import { useFormikContext } from 'formik'
import { ENTITY_TYPES } from '@sales-center/common'
import { ISalesTeam } from '@user/store/salesTeam/typings'
import { useFormFieldActions, useValidateRows } from './hooks'
import { RowErrors } from './RowErrors'
import { BulkReassignFormModel, ValidationCallback } from './typings'

import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Box from '@mui/material/Box'

import { Select } from '@workwave-tidal/core/components'
import Stack from '@mui/material/Stack'
import { useTidalThemeActive } from '@user/features/TidalThemeToggle'
import FormHelperText from '@mui/material/FormHelperText'
import { OwnerSelect } from '@user/components/Owner'
import { selectCurrentUser } from '@user/store/authorization'
import { useSelector } from 'react-redux'

const styles = {
  root: { fontFamily: 'Source Sans Pro SemiBold' },
  spacer: { marginTop: (theme) => theme.spacing(1.5) },
}

type Props = {
  entityType: ENTITY_TYPES
  salesTeams: ISalesTeam[]
  validateRows: ValidationCallback
}

export const FormFields: React.FC<Props> = (props) => {
  const { salesTeams, validateRows, entityType } = props
  const tidalThemeActive = useTidalThemeActive()

  const { spacer } = styles

  const {
    salesTeamId,
    removeServices,
    onChangeSalesTeam,
    loadingUsers,
    isValidating,
    ownerList,
    selectedSalesTeamAccessText,
    positionBasedAccessText,
    salesTeamsBySelectedBranches,
    onChangeRemoveServices,
    closeOrDisqualifiedExists,
  } = useFormFieldActions(entityType, salesTeams)
  const validationResults = useValidateRows(validateRows)
  const { isAdmin } = useSelector(selectCurrentUser)
  const { setFieldValue } = useFormikContext<BulkReassignFormModel>()

  const {
    errorSummary: { hasServicesCount, hasServices },
  } = validationResults

  const RemoveServicesText = useCallback(() => {
    return (
      <Typography variant="body1">
        {`Remove Services for ${hasServicesCount}
        record${hasServicesCount === 1 ? '' : 's'} without Locations`}{' '}
        <br />
        (We will save the details as notes)
      </Typography>
    )
  }, [hasServicesCount])

  useEffect(() => {
    if (hasServicesCount === 0) {
      setFieldValue('removeServices', false)
    }
  }, [hasServicesCount, setFieldValue])

  const height = tidalThemeActive ? '65px' : '54px'
  const currentValue = useMemo(
    () => salesTeams?.find((row) => row?.id == salesTeamId) ?? null,
    [salesTeams, salesTeamId],
  )
  return (
    <>
      <Typography sx={styles.root}>Select Sales Team</Typography>
      <Box sx={{ mt: 1 }}>
        <Select<ISalesTeam>
          items={salesTeamsBySelectedBranches}
          onChange={onChangeSalesTeam}
          value={currentValue}
          required
          getLabel={(option) => `${option?.name} - ${option?.branchName}`}
          label={'Sales Team'}
          testId="reassignSalesTeamFormSalesTeam"
          data-test-id="reassignSalesTeamFormSalesTeam"
          sx={{ height: 6 }}
          listMode="normal"
          optionMode={'custom'}
          renderOption={(props, team: ISalesTeam) => (
            <MenuItem
              sx={{
                height: height,
                minHeight: height,
              }}
              {...props}
              key={team.id}
              value={team.id}
            >
              <Stack>
                <Typography variant="body1">{team?.name}</Typography>
                <Typography variant="body2">{team?.branchName}</Typography>
              </Stack>
            </MenuItem>
          )}
        />
        {currentValue == null && !selectedSalesTeamAccessText && (
          <FormHelperText sx={{ ml: 1 }}>
            Select a Sales Team to see the list of Owners.
          </FormHelperText>
        )}
        {selectedSalesTeamAccessText && (
          <FormHelperText sx={{ ml: 1 }} error>
            {selectedSalesTeamAccessText}
          </FormHelperText>
        )}
      </Box>
      <Typography sx={{ ...spacer, ...styles.root }}>
        Select Sales Team Member
      </Typography>
      <OwnerSelect
        salesTeamId={`${salesTeamId}`}
        errorText={positionBasedAccessText}
        name="assigneeId"
        label="Sales Team Member"
        size="small"
        margin="normal"
        disabled={loadingUsers || isValidating}
        required
        useAdditionalMargin
        unassignedOption
        owners={ownerList}
        testId={`bulkReassign${
          entityType == ENTITY_TYPES.Lead ? 'Lead' : 'Opp'
        }TeamMemberInp`}
        showInactiveUsers={closeOrDisqualifiedExists && isAdmin}
      />
      <RowErrors {...validationResults} entityType={entityType} />
      {hasServices && hasServicesCount > 0 && (
        <Box mr={1} ml={1} mb={1}>
          <FormControlLabel
            label={<RemoveServicesText />}
            control={
              <Checkbox
                color="primary"
                name="removeServices"
                checked={removeServices}
                onChange={onChangeRemoveServices}
              />
            }
          />
        </Box>
      )}
    </>
  )
}
FormFields.displayName = 'FormFields'
